import React, { useState, useEffect, useCallback } from 'react';
import { map } from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import { css } from '@kufox/mui/emotion';
import useTheme from '@kufox/mui/hooks/useTheme';
import Link from '../../components/Link';
import { USER_NAV_KEY_LIST, USER_NAV_KEY } from './const';
import UserBox from '../UserBox';
import AssetsBox from '../AssetsBox';
import OrderBox from '../OrderBox';
import DownloadBox from '../DownloadBox';
import I18nBox from '../I18nBox';
import { namespace } from '../model';
import { getOtcVisible, kcsensorsClick, compose } from '../../common/tools';
import { useLang } from '../../hookTool';
import Point from '../../../static/newHeader/point.svg';

const useStyles = ({ color, inDrawer, navStatus }) => {
  return {
    showItem: css`
      width: 36px;
      height: 36px;
      border-radius: 8px;
      background: rgba(115, 126, 141, 0.08);
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid rgba(115, 126, 141, 0.08);
      background-clip: padding-box !important;
      & > span {
        height: 36px;
        line-height: 36px;
      }
      & .navIcon {
        color: ${color.text};
      }
      &:hover {
        border-color: rgba(115, 126, 141, 0.16);
        opacity: 0.6;
      }
      &:active {
        border: 1px solid rgba(115, 126, 141, 0.08);
        opacity: 1;
      }
    `,
    navUser: css`
      display: flex;
      align-items: center;
      flex-direction: ${inDrawer ? 'column' : 'row'};
      & .navUserItem {
        color: ${color.text};
        cursor: pointer;
        padding-left: ${inDrawer ? '0' : `8px`};
        text-decoration: none !important;
        font-size: 14px;
        display: flex;
        align-items: center;
        justify-content: center;
        width: ${inDrawer ? '100%' : 'auto'};
        white-space: nowrap;
        background-clip: border-box !important;
        &[data-menu='i18n'],
        &[data-menu='currency'],
        &[data-menu='download'] {
          display: ${navStatus !== 1 ? 'none' : 'flex'};
        }
        &[data-menu='order'],
        &[data-menu='asset'],
        &[data-menu='person'] {
          display: ${navStatus === 3 || navStatus === 4 ? 'none' : 'flex'};
        }
        & .signinBtn {
          padding: 6px 12px 7px;
          border: ${inDrawer ? 'none' : `1px solid ${color.primary}`};
          border-radius: 4px;
          line-height: 1.5;
          text-align: center;
          color: ${color.primary};
          width: ${inDrawer ? '100%' : 'auto'};
          margin: ${inDrawer ? '0 24px' : '0'};
          &:hover {
            opacity: 0.6;
          }
          &:active {
            opacity: 1;
          }
        }
        & .signUpBtn {
          padding: 6px 12px 7px;
          border: 1px solid ${color.primary};
          border-radius: 4px;
          line-height: 1.5;
          text-align: center;
          color: #fff;
          background: ${color.primary};
          width: ${inDrawer ? '100%' : 'auto'};
          margin: ${inDrawer ? '0 24px' : '0'};
          margin-top: ${inDrawer ? '24px' : 0};
          position: relative;
          & > img {
            width: 10px;
            height: 10px;
            position: absolute;
            display: none;
            top: -7px;
            right: -7px;
          }
          &:hover {
            opacity: 0.6;
          }
          &:active {
            opacity: 1;
            & > img {
              display: block;
            }
          }
        }
      }
    `,
    userBox: css`
      display: flex;
      margin-right: 0;
      flex-direction: ${inDrawer ? 'column' : 'row'};
      width: ${inDrawer ? '100%' : 'auto'};
    `,
    userBoxComponent: css`
      margin-left: 8px;
    `,
  };
};

export default (props) => {
  const {
    currentLang,
    currency,
    menuConfig = USER_NAV_KEY_LIST,
    userInfo,
    customColors = {},
    hostConfig,
    onSinin,
    onSinup,
    inDrawer,
    handleShowDrawer,
    navStatus,
    signsRef,
  } = props;
  const { KUCOIN_HOST } = hostConfig;
  const theme = useTheme();
  const color = { ...theme.colors, ...customColors };
  const cls = useStyles({ color, inDrawer, navStatus });

  const { t } = useLang();
  const [otcVisible, setOtcVisible] = useState(false);

  const { otcLanguageList } = useSelector((state) => state[namespace]);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!otcLanguageList) {
      dispatch({ type: `${namespace}/pullOtcLanguageList` });
    }
  }, []);

  useEffect(() => {
    const visible = getOtcVisible(otcLanguageList, currentLang, currency);
    setOtcVisible(visible);
  }, [currentLang, currency, otcLanguageList]);

  const handleSinin = (e) => {
    if (onSinin) {
      e.stopPropagation();
      e.preventDefault();
      onSinin();
    }
    kcsensorsClick(['login', '1']);
  };
  const handleSinup = (e) => {
    if (onSinup) {
      e.stopPropagation();
      e.preventDefault();
      onSinup();
    }
    kcsensorsClick(['register', '1']);
  };
  const getSpm = useCallback((spmSuffix) => {
    const spm_id = compose(spmSuffix);
    return spm_id ? `?spm=${spm_id}` : '';
  }, []);

  const loginHref = onSinin ? undefined : `${KUCOIN_HOST}/ucenter/signin${getSpm(['login', '1'])}`;
  const signHref = onSinup
    ? undefined
    : `${KUCOIN_HOST}/ucenter/signup${getSpm(['register', '1'])}`;

  return (
    <div css={cls.navUser}>
      {!userInfo && ((navStatus !== 3 && navStatus !== 4) || inDrawer) && (
        <div css={cls.userBox} data-lang={currentLang} ref={signsRef}>
          <Link href={loginHref} className="navUserItem" onClick={handleSinin} data-modid="login">
            <div className="signinBtn">{t('login')}</div>
          </Link>
          <Link href={signHref} className="navUserItem" onClick={handleSinup}>
            <div className="signUpBtn" data-modid="register">
              {t('sign.up')}
              <img src={Point} alt="" />
            </div>
          </Link>
        </div>
      )}
      {map(menuConfig, (menu, index) => {
        if (typeof menu === 'string' && USER_NAV_KEY[menu.toUpperCase()]) {
          let comp;
          switch (menu) {
            case USER_NAV_KEY.ORDER:
              comp = userInfo ? (
                <OrderBox {...props} otcVisible={otcVisible} css={cls.showItem} />
              ) : null;
              break;
            case USER_NAV_KEY.PERSON:
              comp = userInfo ? (
                <UserBox {...props} userInfo={userInfo} handleShowDrawer={handleShowDrawer} />
              ) : null;
              break;
            case USER_NAV_KEY.ASSET:
              comp = userInfo ? (
                <AssetsBox {...props} otcVisible={otcVisible} css={cls.showItem} />
              ) : null;
              break;
            case USER_NAV_KEY.I18N:
              comp = <I18nBox {...props} showType="lang" css={cls.showItem} />;
              break;
            case USER_NAV_KEY.CURRENCY:
              comp = <I18nBox {...props} css={cls.showItem} />;
              break;
            case USER_NAV_KEY.DOWNLOAD:
              comp = <DownloadBox {...props} />;
              break;
            default:
              comp = null;
          }
          return comp ? (
            <div className="navUserItem" key={menu} data-lang={currentLang} data-menu={menu}>
              {comp}
            </div>
          ) : (
            comp
          );
        }
        return (
          <div
            className="navUserItem"
            key={`menu${index}`}
            data-lang={currentLang}
            data-menu="notice"
          >
            {menu}
          </div>
        );
      })}
      {userInfo && !inDrawer && (navStatus === 3 || navStatus === 4) ? (
        <div css={cls.userBoxComponent} data-lang={currentLang} data-menu={USER_NAV_KEY.PERSON}>
          <UserBox {...props} userInfo={userInfo} handleShowDrawer={handleShowDrawer} minNav />
        </div>
      ) : null}
    </div>
  );
};
